import { useMemo } from 'react';
import { usePreviewCollectionRequestQuery } from '@frontend/api-collections';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { useBulkCollectionSelectAllShallowStore } from '../../../../../../store/bulk-collections-select-all-store';
import { mapPatientBalance } from '../../../../mapper';
import { getFilterString } from '../../../../utils';
import { useValidateUsersShallowStore } from '../../../stores/validate-users-store';

export const DEFAULT_PAGE_SIZE = 10;

type UseBaseQueryParams = {
  enabled: boolean;
  pageToken?: string;
  pageSize?: number;
  orderBy?: string;
  keepPreviousData?: boolean;
};

export const usePreviewQueryBase = ({
  enabled,
  pageToken,
  pageSize = DEFAULT_PAGE_SIZE,
  orderBy,
  keepPreviousData = false,
}: UseBaseQueryParams) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const alerts = useAlert();
  const {
    filters: { balanceType, searchValue },
    included,
    excluded,
    isAllSelected,
  } = useBulkCollectionSelectAllShallowStore('filters', 'included', 'excluded', 'isAllSelected');
  const { filters: internalFilters } = useValidateUsersShallowStore('filters');

  const { data: rawData, isFetching } = usePreviewCollectionRequestQuery({
    request: {
      locationIds: internalFilters.location ? [internalFilters.location] : selectedLocationIds,
      pageToken,
      pageSize,
      orderBy,
      filter:
        balanceType.length > 0 || internalFilters.searchValue || searchValue
          ? getFilterString(balanceType, internalFilters.searchValue || searchValue)
          : undefined,
      includedPersonIds: included,
      excludedPersonIds: excluded,
      selectAll: isAllSelected && balanceType.length === 0 && !(internalFilters.searchValue || searchValue),
    },
    options: {
      enabled: enabled && selectedLocationIds.length > 0,
      keepPreviousData,
      // refetchOnMount: 'always',
      onError: () => {
        alerts.error(t('Error fetching collections'));
      },
    },
  });

  const data = useMemo(() => (rawData?.patientBalances ?? []).map(mapPatientBalance), [rawData?.patientBalances]);
  const nextPageToken = rawData?.nextPageToken;

  return {
    data,
    isFetching,
    nextPageToken,
  };
};
